import { LoanType } from '@apply/constants/src/loans/LoanType';
import type {
  BllMode,
  ExperienceLimitTier,
  LendingCategory,
  LoanBucket,
  LoanProduct,
} from '../types';

/**
 * `B18`: the repayment bonus credits 50% of a borrower's total repaid value toward
 * their lending limit ("increases by 50% of repayments with BFI").
 */
export const REPAYMENT_CREDIT_RATE = 0.5;

/**
 * `Surplus BLL!B12` — portfolio-wide cap on total surplus (commitment in excess of
 * borrowers' lending limits, granted as memo exceptions).
 */
export const SURPLUS_LIMIT = 15_000_000;

/**
 * Product → fee-treatment mode.
 *
 * Direct-lending products (`TEMPLATE - CORE`) finance BFI fees, so the current
 * commitment nets them out (B23 = B9 − E9). Participation products
 * (`TEMPLATE - SHEPHERDS`) do not (B23 = B9).
 */
export const PRODUCT_MODE: Record<LoanProduct, BllMode> = {
  DV: 'direct',
  DR: 'direct',
  SF: 'participation',
  CBL: 'participation',
  LYNK: 'participation',
} as const;

/**
 * Experience → limit lookup, replicating `Data!O21:R221`.
 *
 * The workbook has one row per integer score 0–200, but the values only change at
 * three boundaries, so the table is stored as bands keyed by inclusive lower bound
 * ({@link ExperienceLimitTier.minExperience}). {@link lookupExperienceTier} selects
 * the highest band whose bound is ≤ the score, which reproduces the workbook's
 * exact-match VLOOKUP for every score in 0–200 and extends naturally beyond 200.
 *
 * Must stay sorted ascending by `minExperience`.
 */
export const EXPERIENCE_LIMIT_TABLE: readonly ExperienceLimitTier[] = [
  { minExperience: 0,  initialBLL: 3_000_000, maxBLL: 4_000_000, type: 'Low-Experienced' },
  { minExperience: 10, initialBLL: 3_000_000, maxBLL: 4_000_000, type: 'Moderate-Experienced' },
  { minExperience: 20, initialBLL: 4_000_000, maxBLL: 5_000_000, type: 'Well-Experienced' },
] as const;

// ── Category breakdown ────────────────────────────────────────────────────────
//
// UNCONFIRMED WITH BUSINESS. Everything in this section comes from reading the UI
// mockups (`screenshots/lending-limits/`) rather than from the workbook, which has no
// notion of categories. Each item below notes what is assumed.

/**
 * Bucket a loan is sorted into when {@link LoanRecord.loanType} is absent — the case
 * for the workbook-derived fixtures, which predate categorisation.
 */
export const UNBUCKETED: LoanBucket = 'construction';

/**
 * Loan type → detail-table bucket.
 *
 * ASSUMED, two entries:
 *   - `Model` appears in no mockup row; it is grouped with Construction here.
 *   - The three LOC types (`MasterNote` and its two sub-note flavours) all map to
 *     `lineOfCredit`, so a sub-note does *not* land in Core alongside ordinary
 *     construction loans. The BE already collapses each LOC to a single row, so the
 *     sub-note entries should rarely be reached.
 *
 * `Bridge` maps to `retainedBridge` because the BE sends no sold-vs-retained flag —
 * see {@link BRIDGE_RETAINED_CAP}. Once that flag exists, sold bridge loans must be
 * routed to `soldBridge` here.
 */
export const LOAN_TYPE_BUCKET: Record<LoanType, LoanBucket> = {
  [LoanType.Construction]: 'construction',
  [LoanType.Rental]: 'rental',
  [LoanType.BuildRent]: 'buildFixRent',
  [LoanType.Model]: 'construction',
  [LoanType.Land]: 'land',
  [LoanType.FixFlip]: 'fixFlip',
  [LoanType.MasterNote]: 'lineOfCredit',
  [LoanType.SubNoteConstruction]: 'lineOfCredit',
  [LoanType.SubNoteFixFlip]: 'lineOfCredit',
  [LoanType.Bridge]: 'retainedBridge',
} as const;

/** Bucket → summary category. Drives the Guarantor Summary roll-up. */
export const BUCKET_CATEGORY: Record<LoanBucket, LendingCategory> = {
  construction: 'core',
  fixFlip: 'core',
  land: 'core',
  soldBridge: 'bridge',
  retainedBridge: 'bridge',
  lineOfCredit: 'loc',
  rental: 'dscr',
  buildFixRent: 'dscr',
} as const;

/** Every bucket, in the order its rows appear in the UI. */
export const LOAN_BUCKETS: readonly LoanBucket[] = [
  'construction',
  'fixFlip',
  'land',
  'soldBridge',
  'retainedBridge',
  'lineOfCredit',
  'rental',
  'buildFixRent',
] as const;

/**
 * Ceiling on retained bridge exposure counted against the Bridge category; the
 * remainder rolls into Core as "Excess Bridge" (the mockup's "* Amount over $1 MM from
 * Bridge below").
 *
 * ASSUMED: the cap is applied to outstanding balance, and only to *retained* bridge.
 * The BE sends no sold-vs-retained flag today, so every bridge loan is treated as
 * retained and the `soldBridge` bucket stays empty.
 */
export const BRIDGE_RETAINED_CAP = 1_000_000;

/**
 * "Max Surplus" shown on every Guarantor Summary row.
 *
 * ASSUMED, and in tension with the workbook: the mockup shows a flat $1.5MM per
 * category row, whereas the workbook models surplus as a single portfolio-wide
 * {@link SURPLUS_LIMIT} pool of $15MM. Business has not yet reconciled the two.
 */
export const MAX_SURPLUS_PER_CATEGORY = 1_500_000;
